<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .ai-answer {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: utils.in_rem(20px) utils.in_rem(8px) 0 2px;
    gap: utils.in_rem(16px);
    overflow-y: auto;
    scrollbar-gutter: stable;

    .skeleton-message {
      @include mixins.skeleton(
        (
          background-color: colors.get(interfaceSwGrey_5),
          border-radius: 8px,
          height: utils.in_rem(32px),
        )
      );
    }

    .user {
      align-items: center;
      align-self: flex-end;
      border-radius: 8px;
      background-color: colors.get(interfaceSwGrey_5);
      display: flex;
      padding: utils.in_rem(6px) utils.in_rem(12px);
      gap: utils.in_rem(24px);
      justify-content: center;
      @include mixins.body2();
      @include colors.color(black_90);
      white-space: pre-wrap; /* Preserve whitespace and line breaks */

      &.skeleton-message {
        &.user-first {
          width: 70%;
        }

        &.user-second {
          width: 40%;
        }
      }
    }

    .assistant {
      width: 100%;
      align-self: flex-start;
      display: flex;
      flex-direction: column;
      margin-bottom: utils.in_rem(24px);
      gap: utils.in_rem(8px);
      @include mixins.body2();

      &.skeleton-message {
        width: 100%;
      }

      p {
        &.ai-thinking {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        &:has(.ai-thinking) {
          background-image:
            linear-gradient(
              colors.get(interfaceWhite),
              colors.get(interfaceWhite)
            ),
            colors.get(aiGradient);
          background-origin: border-box;
          background-clip: padding-box, border-box;

          > .ai-thinking {
            animation: text-gradient 1.5s linear infinite;
            background-image: colors.get(aiGradient);
            background-clip: text;
            color: transparent;
          }
        }
      }

      .button-group {
        display: flex;
        gap: utils.in_rem(4px);
      }
    }
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Spinner from "@sourcewhale/shared/svelte-design-system/components/atoms/Spinner.svelte";
  import Node from "@sourcewhale/shared/svelte-design-system/components/utils/Node.svelte";
  import DOMPurify from "dompurify";

  import { showToast } from "@sourcewhale/core";
  import { contactChatFeedback } from "../../../api/contact-chat";

  let {
    apiStore,
    promptStore,
    chatStore,
    thinkingStore,
    thinking,
    examples,
    showSkeleton = false,
  } = $props();
  let localFeedback = $state({});

  $effect(() => {
    if ($apiStore) {
      localFeedback = {};
    }
  });

  const htmlToText = (html) => {
    const tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = html;
    const text = tempDivElement.textContent || tempDivElement.innerText || "";
    tempDivElement.remove();
    return text;
  };

  const extractTextFromNode = (node) => {
    if (!node) return "";
    return typeof node === "string"
      ? node
      : (node.children || [])
          .map(extractTextFromNode)
          .filter(Boolean)
          .join(" ");
  };

  const copyOnclick = async (answer) => {
    const text =
      typeof answer === "string"
        ? htmlToText(answer) // Legacy format: HTML string
        : extractTextFromNode(answer); // Modern format: Extract from NODE_SCHEMA

    await navigator.clipboard.writeText(text.trim());
    showToast({ type: "success", title: "Message copied to clipboard" });
  };

  const submitFeedback = async (msg, feedback) => {
    localFeedback[msg.msgId] = feedback;
    await contactChatFeedback({
      chatId: msg.chatId,
      msgId: msg.msgId,
      feedback,
    });
  };

  let thinkEl = $state(null);
  $effect(() => {
    if (thinkEl?.scrollIntoView) {
      thinkEl.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  });

  let behavior = $state("instant");
  let lastEl = $state(null);
  $effect(() => {
    if (lastEl?.scrollIntoView) {
      lastEl.scrollIntoView({ block: "start", behavior });
      behavior = "smooth";
    }
  });

  /**
   * Injects Svelte-generated CSS classes into legacy HTML to ensure styling
   * consistency. Prior to writing this code, I took sea sickness medication as
   * recommended by Ben. It was necessary.
   */
  const injectSvelteClasses = (htmlString) => {
    if (!htmlString) return htmlString;

    const assistantElement = document.querySelector(".assistant");
    if (!assistantElement) return htmlString;

    const svelteClasses = Array.from(assistantElement.classList)
      .filter((className) => className.startsWith("svelte-"))
      .join(" ");

    return svelteClasses
      ? htmlString.replace(/<(\w+)([^>]*?)>/g, (match, tag, attributes) => {
          if (attributes.includes("class=")) {
            return match.replace(
              /class="([^"]*)"/,
              `class="$1 ${svelteClasses}"`,
            );
          }
          return `<${tag}${attributes} class="${svelteClasses}">`;
        })
      : htmlString;
  };
</script>

<div class="ai-answer">
  {#if showSkeleton}
    <!-- Show skeleton messages -->
    <div class="user skeleton-message user-first"></div>
    <div class="assistant">
      <div class="skeleton-message"></div>
    </div>
    <div class="user skeleton-message user-second"></div>
    <div class="assistant">
      <div class="skeleton-message"></div>
    </div>
  {:else}
    {#each $promptStore as msg, i}
      {#if msg.role === "user"}
        <div class="user">{msg.content}</div>
      {:else if msg.role === "assistant"}
        {@const feedback = localFeedback[msg.msgId] || msg.feedback || 0}

        <div class="assistant">
          {#if $chatStore.regenerate && $thinking && i === $promptStore.length - 1}
            <p class="ai-thinking">
              <Spinner class="color-aiIconPurple" />
              <span class="ai-thinking">{$thinkingStore ?? ""}</span>
            </p>
          {/if}

          <!-- Handle both new NODE_SCHEMA format and legacy HTML strings -->
          {#if typeof msg.content === "string"}
            <!-- Legacy format: Raw HTML from pre-NODE_SCHEMA responses -->
            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
            {@html injectSvelteClasses(DOMPurify.sanitize(msg.content))}
          {:else}
            <!-- Modern format: NODE_SCHEMA JSON structure -->
            <Node {...msg.content} />
          {/if}

          <div class="button-group">
            <Button
              class={$thinking ? "disabled" : ""}
              disabled={$thinking}
              size="xs"
              kind={feedback === 1 ? "tertiary-blue" : "ghost-grey"}
              leadingIcon="fa-thumbs-up"
              onclick={() => submitFeedback(msg, 1)}
            />
            <Button
              class={$thinking ? "disabled" : ""}
              disabled={$thinking}
              size="xs"
              kind={feedback === -1 ? "tertiary-blue" : "ghost-grey"}
              leadingIcon="fa-thumbs-down"
              onclick={() => submitFeedback(msg, -1)}
            />
            <Button
              size="xs"
              kind="ghost-grey"
              leadingIcon="fa-copy"
              onclick={() => copyOnclick(msg.content)}
            />

            {#if i === $promptStore.length - 1}
              <Button
                bind:el={lastEl}
                class={$thinking ? "disabled" : ""}
                disabled={$thinking}
                size="xs"
                kind="ghost-grey"
                leadingIcon="fa-refresh"
                onclick={() => chatStore.regenerate()}
              />
            {/if}
          </div>
        </div>
      {/if}
    {/each}
  {/if}
  {#if $thinking && !$chatStore.regenerate}
    <div class="user">{$chatStore.task}</div>
    <div bind:this={thinkEl} class="assistant">
      <p class="ai-thinking">
        <Spinner class="color-aiIconPurple" />
        <span class="ai-thinking">{$thinkingStore}</span>
      </p>
    </div>
  {/if}
  {@render examples?.()}
</div>
